import React from 'react';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';

import styles from './EmptyStateHint.module.scss';

const cx: CX = classnames.bind(styles);

interface EmptyStateHintProps {
    type: 'types' | 'brands' | 'locations' | 'documents';
    title: string;
    description: string;
}

function EmptyStateHint({ type, title, description }: EmptyStateHintProps) {
    const navigate = useNavigate();

    const handleConfigureClick = () => {
        navigate(ROUTES.admin.appSettings.path);
    };

    const shouldShowConfigureButton = () => type !== 'documents';

    const getIcon = () => {
        switch (type) {
            case 'types':
                return (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                    </svg>
                );
            case 'brands':
                return (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                );
            case 'locations':
                return (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                    </svg>
                );
            case 'documents':
                return (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                    </svg>
                );
            default:
                return null;
        }
    };

    return (
        <div className={cx('empty-state-hint')}>
            <div className={cx('content')}>
                <div className={cx('icon-container')}>
                    {getIcon()}
                </div>
                <h3 className={cx('title')}>{title}</h3>
                <p className={cx('description')}>{description}</p>
                {shouldShowConfigureButton() && (
                    <div className={cx('actions')}>
                        <BasicButton
                            title="Configure in App Settings"
                            onClick={handleConfigureClick}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default EmptyStateHint; 